<template>
  <div>
    <AdminNav />
    <main class="page">
      <header class="page-header">
        <div>
          <h2 class="page-title">Justificatifs</h2>
          <p class="subtitle">Pieces de ventes, achats, charges, paiements et documents administratifs.</p>
        </div>
        <button class="btn btn-secondary" @click="exporterCsv">Export CSV</button>
      </header>

      <p class="success-message" v-if="message">{{ message }}</p>
      <p class="error-message" v-if="erreur">{{ erreur }}</p>
      <p class="notice" v-if="uploadLimite">{{ uploadLimite }}</p>

      <section class="filters">
        <select v-model="filtres.type" class="input" @change="chargerJustificatifs">
          <option value="">Tous les types</option>
          <option v-for="type in types" :key="type" :value="type">{{ libelleType(type) }}</option>
        </select>
        <select v-model="filtres.categorie" class="input" @change="chargerJustificatifs">
          <option value="">Toutes catégories</option>
          <option v-for="categorie in categories" :key="categorie">{{ categorie }}</option>
        </select>
        <select v-model="filtres.statut" class="input" @change="chargerJustificatifs">
          <option value="">Tous statuts</option>
          <option value="actif">Actif</option>
          <option value="archive">Archivé</option>
          <option value="supprime">Supprimé</option>
        </select>
        <input v-model="filtres.date_debut" class="input" type="date" @change="chargerJustificatifs" />
        <input v-model="filtres.date_fin" class="input" type="date" @change="chargerJustificatifs" />
      </section>

      <div class="layout">
        <section class="panel list-panel">
          <div class="panel-head">
            <h3>Liste</h3>
            <span class="badge">{{ justificatifs.length }}</span>
          </div>
          <div class="empty" v-if="!justificatifs.length">Aucun justificatif.</div>
          <article
            v-for="justificatif in justificatifs"
            :key="justificatif.id"
            class="item-card"
            :class="{ active: form.id === justificatif.id }"
            @click="editer(justificatif)"
          >
            <div>
              <strong>{{ justificatif.libelle }}</strong>
              <small>{{ libelleType(justificatif.type) }} · {{ justificatif.categorie || 'Sans catégorie' }}</small>
              <small>{{ formatDate(justificatif.date_document) }} · {{ justificatif.facture_numero || justificatif.charge_libelle || justificatif.reference_paiement || 'Non lié' }}</small>
            </div>
            <div class="item-meta">
              <em>{{ formatMoney(justificatif.montant_ttc) }}</em>
              <span class="badge" :class="'badge-' + justificatif.statut">{{ libelleStatut(justificatif.statut) }}</span>
              <button
                v-if="justificatif.has_file"
                class="btn btn-secondary small-btn"
                @click.stop="telecharger(justificatif)"
              >
                Télécharger
              </button>
            </div>
          </article>
        </section>

        <section class="panel form-panel">
          <div class="panel-head">
            <h3>{{ form.id ? 'Modifier justificatif' : 'Ajouter justificatif' }}</h3>
            <button class="btn btn-secondary" @click="nouveau">Nouveau</button>
          </div>

          <div class="grid">
            <label class="field">Type
              <select v-model="form.type" class="input">
                <option v-for="type in types" :key="type" :value="type">{{ libelleType(type) }}</option>
              </select>
            </label>
            <label class="field">Catégorie
              <select v-model="form.categorie" class="input">
                <option value="">Non définie</option>
                <option v-for="categorie in categories" :key="categorie">{{ categorie }}</option>
              </select>
            </label>
            <label class="field">Libellé
              <input v-model="form.libelle" class="input" />
            </label>
            <label class="field">Date document
              <input v-model="form.date_document" class="input" type="date" />
            </label>
            <label class="field">Montant TTC
              <input v-model.number="form.montant_ttc" class="input" type="number" min="0" step="0.01" />
            </label>
            <label class="field">Facture ID
              <input v-model="form.facture_id" class="input" />
            </label>
            <label class="field">Charge ID
              <input v-model="form.charge_id" class="input" />
            </label>
            <label class="field">Paiement ID
              <input v-model="form.paiement_id" class="input" />
            </label>
          </div>

          <label class="field">Description
            <textarea v-model="form.description" class="input textarea"></textarea>
          </label>
          <label class="field">Notes
            <textarea v-model="form.notes" class="input textarea"></textarea>
          </label>
          <label class="field" v-if="!form.id">Fichier PDF ou image
            <input class="input file-input" type="file" accept=".pdf,.png,.jpg,.jpeg,.webp,application/pdf,image/png,image/jpeg,image/webp" @change="selectionnerFichier" />
            <small class="file-help">PDF, PNG, JPG, JPEG ou WEBP - 10 Mo max.</small>
          </label>

          <div class="file-row" v-if="fichier || form.fichier_nom_original">
            <span>{{ fichier ? fichier.name : form.fichier_nom_original }}</span>
            <small>{{ fichier ? formatFileSize(fichier.size) : formatFileSize(form.taille_octets) }}</small>
            <button class="btn btn-secondary" @click="telecharger(form)" :disabled="!form.has_file">Télécharger</button>
          </div>

          <div class="actions">
            <button class="btn btn-primary" @click="enregistrer" :disabled="loading">{{ loading ? 'Enregistrement...' : 'Enregistrer' }}</button>
            <button class="btn btn-secondary" @click="archiver" :disabled="!form.id || loading">Archiver</button>
            <button class="btn btn-danger" @click="supprimer" :disabled="!form.id || loading">Supprimer</button>
          </div>
        </section>
      </div>
    </main>
  </div>
</template>

<script>
import AdminNav from '../../components/AdminNav.vue'
import adminApi from '../../adminApi'

export default {
  name: 'AdminJustificatifs',
  components: { AdminNav },
  data() {
    return {
      types: ['vente', 'achat', 'charge', 'paiement', 'administratif', 'autre'],
      categories: ['Facture client', 'Preuve paiement', 'Ticket caisse', 'Facture fournisseur', 'Matériel', 'Impression cartes', 'Abonnement logiciel', 'Hébergement', 'Banque', 'Déplacement', 'Comptable', 'Administratif', 'Autre'],
      justificatifs: [],
      fichier: null,
      fichierErreur: '',
      loading: false,
      erreur: '',
      message: '',
      uploadLimite: '',
      filtres: { type: '', categorie: '', statut: 'actif', date_debut: '', date_fin: '' },
      form: this.justificatifVide()
    }
  },
  methods: {
    justificatifVide() {
      return {
        id: '',
        type: 'vente',
        categorie: 'Facture client',
        libelle: '',
        description: '',
        montant_ttc: '',
        date_document: new Date().toISOString().slice(0, 10),
        facture_id: '',
        charge_id: '',
        paiement_id: '',
        commercant_id: '',
        notes: '',
        statut: 'actif',
        fichier_nom_original: '',
        taille_octets: null,
        has_file: false
      }
    },
    appliquerPrefill() {
      const query = this.$route.query || {}
      this.form = {
        ...this.justificatifVide(),
        type: query.type || 'vente',
        categorie: query.categorie || (query.type === 'paiement' ? 'Preuve paiement' : 'Facture client'),
        facture_id: query.facture_id || '',
        charge_id: query.charge_id || '',
        paiement_id: query.paiement_id || '',
        libelle: query.libelle || ''
      }
    },
    libelleType(type) {
      return { vente: 'Vente', achat: 'Achat', charge: 'Charge', paiement: 'Paiement', administratif: 'Administratif', autre: 'Autre' }[type] || type
    },
    libelleStatut(statut) {
      return { actif: 'Actif', archive: 'Archivé', supprime: 'Supprimé' }[statut] || statut
    },
    formatDate(value) {
      return value ? new Date(value).toLocaleDateString('fr-FR') : 'Sans date'
    },
    formatMoney(value) {
      if (value === null || value === undefined || value === '') return '-'
      return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(Number(value || 0))
    },
    formatDateInput(value) {
      return value ? String(value).slice(0, 10) : ''
    },
    paramsFiltres() {
      return Object.fromEntries(Object.entries(this.filtres).filter(([, value]) => value))
    },
    async chargerJustificatifs() {
      this.erreur = ''
      const res = await adminApi.get('/justificatifs', { params: this.paramsFiltres() })
      this.justificatifs = res.data || []
    },
    nouveau() {
      this.form = this.justificatifVide()
      this.fichier = null
      this.fichierErreur = ''
      this.message = ''
      this.erreur = ''
    },
    editer(justificatif) {
      this.form = {
        ...this.justificatifVide(),
        ...justificatif,
        date_document: this.formatDateInput(justificatif.date_document),
        montant_ttc: justificatif.montant_ttc ?? '',
        facture_id: justificatif.facture_id || '',
        charge_id: justificatif.charge_id || '',
        paiement_id: justificatif.paiement_id || '',
        commercant_id: justificatif.commercant_id || ''
      }
      this.fichier = null
      this.fichierErreur = ''
    },
    selectionnerFichier(event) {
      const file = event.target.files?.[0] || null
      this.fichier = null
      this.fichierErreur = ''
      this.erreur = ''
      if (!file) return

      const allowedTypes = ['application/pdf', 'image/png', 'image/jpeg', 'image/webp']
      const allowedExtensions = ['pdf', 'png', 'jpg', 'jpeg', 'webp']
      const extension = file.name.split('.').pop()?.toLowerCase()
      if (!allowedTypes.includes(file.type) || !allowedExtensions.includes(extension)) {
        this.fichierErreur = 'Format interdit. Formats acceptés : PDF, PNG, JPG, JPEG, WEBP.'
        this.erreur = this.fichierErreur
        event.target.value = ''
        return
      }
      if (file.size > 10 * 1024 * 1024) {
        this.fichierErreur = 'Fichier trop volumineux. Taille maximale : 10 Mo.'
        this.erreur = this.fichierErreur
        event.target.value = ''
        return
      }
      this.fichier = file
    },
    formatFileSize(value) {
      const size = Number(value || 0)
      if (!size) return 'Taille inconnue'
      if (size < 1024 * 1024) return `${Math.round(size / 1024)} Ko`
      return `${(size / (1024 * 1024)).toFixed(1).replace('.', ',')} Mo`
    },
    payloadJson() {
      return {
        type: this.form.type,
        categorie: this.form.categorie,
        libelle: this.form.libelle,
        description: this.form.description,
        montant_ttc: this.form.montant_ttc,
        date_document: this.form.date_document,
        facture_id: this.form.facture_id,
        charge_id: this.form.charge_id,
        paiement_id: this.form.paiement_id,
        commercant_id: this.form.commercant_id,
        notes: this.form.notes
      }
    },
    payloadFormData() {
      const data = new FormData()
      Object.entries(this.payloadJson()).forEach(([key, value]) => data.append(key, value ?? ''))
      if (this.fichier) data.append('fichier', this.fichier)
      return data
    },
    async enregistrer() {
      this.loading = true
      this.erreur = ''
      this.message = ''
      this.uploadLimite = ''
      if (this.fichierErreur) {
        this.erreur = this.fichierErreur
        this.loading = false
        return
      }
      try {
        const useMultipart = Boolean(this.fichier && !this.form.id)
        const res = this.form.id
          ? await adminApi.put(`/justificatifs/${this.form.id}`, this.payloadJson())
          : await adminApi.post('/justificatifs', useMultipart ? this.payloadFormData() : this.payloadJson())
        await this.chargerJustificatifs()
        this.editer(res.data)
        this.message = 'Justificatif enregistré.'
      } catch (err) {
        if (err.response?.status === 501) this.uploadLimite = err.response.data?.details || 'Upload fichier indisponible.'
        this.erreur = err.response?.data?.error || 'Impossible d’enregistrer le justificatif.'
      } finally {
        this.loading = false
      }
    },
    async changerStatut(statut) {
      this.loading = true
      this.erreur = ''
      try {
        const res = await adminApi.patch(`/justificatifs/${this.form.id}/statut`, { statut })
        await this.chargerJustificatifs()
        this.editer(res.data)
      } catch (err) {
        this.erreur = err.response?.data?.error || 'Impossible de changer le statut.'
      } finally {
        this.loading = false
      }
    },
    archiver() {
      this.changerStatut('archive')
    },
    async supprimer() {
      if (!confirm('Supprimer logiquement ce justificatif ?')) return
      this.loading = true
      this.erreur = ''
      try {
        const res = await adminApi.delete(`/justificatifs/${this.form.id}`)
        await this.chargerJustificatifs()
        this.editer(res.data)
        this.message = 'Justificatif marqué supprimé.'
      } catch (err) {
        this.erreur = err.response?.data?.error || 'Impossible de supprimer le justificatif.'
      } finally {
        this.loading = false
      }
    },
    async telecharger(justificatif) {
      try {
        const res = await adminApi.get(`/justificatifs/${justificatif.id}/download`, { responseType: 'blob' })
        const url = window.URL.createObjectURL(new Blob([res.data], { type: justificatif.mime_type || 'application/octet-stream' }))
        const link = document.createElement('a')
        link.href = url
        link.setAttribute('download', justificatif.fichier_nom_original || 'justificatif')
        document.body.appendChild(link)
        link.click()
        link.remove()
        window.URL.revokeObjectURL(url)
      } catch (err) {
        this.erreur = err.response?.data?.error || 'Impossible de télécharger le fichier.'
      }
    },
    async exporterCsv() {
      try {
        const res = await adminApi.get('/justificatifs/export.csv', { params: this.paramsFiltres(), responseType: 'blob' })
        const url = window.URL.createObjectURL(new Blob([res.data], { type: 'text/csv;charset=utf-8' }))
        const link = document.createElement('a')
        link.href = url
        link.setAttribute('download', 'justificatifs.csv')
        document.body.appendChild(link)
        link.click()
        link.remove()
        window.URL.revokeObjectURL(url)
      } catch (err) {
        this.erreur = err.response?.data?.error || 'Impossible d’exporter les justificatifs.'
      }
    }
  },
  mounted() {
    this.appliquerPrefill()
    this.chargerJustificatifs().catch(err => {
      this.erreur = err.response?.data?.error || 'Impossible de charger les justificatifs.'
    })
  }
}
</script>

<style scoped>
.page {
  width: 100%;
  max-width: 1280px;
  margin: 0 auto;
  padding: 14px;
  box-sizing: border-box;
}
.page-header, .filters, .layout, .grid, .actions {
  display: grid;
  gap: 12px;
}
.page-header {
  margin-bottom: 14px;
}
.page-title {
  margin: 0 0 4px;
  color: #1a202c;
  font-size: 22px;
  font-weight: 700;
}
.subtitle, .notice {
  margin: 0;
  color: #718096;
  font-size: 13px;
  line-height: 1.4;
}
.filters {
  margin-bottom: 14px;
}
.panel {
  min-width: 0;
  padding: 14px;
  border-radius: 8px;
  background: white;
  box-shadow: 0 2px 8px rgba(0,0,0,0.06);
}
.panel-head {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 10px;
  margin-bottom: 12px;
}
.panel h3 {
  margin: 0;
  color: #2d3748;
  font-size: 16px;
}
.item-card {
  display: grid;
  gap: 10px;
  width: 100%;
  margin-bottom: 8px;
  padding: 12px;
  border: 1px solid #edf2f7;
  border-radius: 8px;
  background: #fff;
  box-sizing: border-box;
  cursor: pointer;
}
.item-card.active, .item-card:hover {
  border-color: #2d3748;
  background: #f7fafc;
}
.item-card div, .item-meta, .field, .file-row {
  display: grid;
  gap: 5px;
  min-width: 0;
}
.item-card strong {
  color: #1a202c;
}
.item-card small, .field {
  color: #718096;
  font-size: 12px;
  font-weight: 700;
}
.item-meta em {
  color: #1a202c;
  font-style: normal;
  font-weight: 700;
}
.input {
  width: 100%;
  min-height: 40px;
  padding: 9px 11px;
  border: 1.5px solid #e2e8f0;
  border-radius: 8px;
  background: #fff;
  font-size: 14px;
  box-sizing: border-box;
}
.textarea {
  min-height: 78px;
  resize: vertical;
}
.file-input {
  padding: 8px;
}
.file-help {
  color: #718096;
  font-size: 12px;
  font-weight: 500;
}
.btn {
  min-height: 40px;
  padding: 10px 14px;
  border: 0;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;
}
.btn:disabled {
  opacity: 0.55;
  cursor: not-allowed;
}
.btn-primary { background: #2d3748; color: white; }
.btn-secondary { background: #edf2f7; color: #4a5568; }
.btn-danger { background: #fed7d7; color: #742a2a; }
.small-btn {
  min-height: 32px;
  padding: 7px 10px;
  font-size: 12px;
}
.badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: fit-content;
  border-radius: 999px;
  padding: 4px 10px;
  background: #edf2f7;
  color: #4a5568;
  font-size: 12px;
  font-weight: 700;
}
.badge-actif { background: #c6f6d5; color: #22543d; }
.badge-archive { background: #fff3cd; color: #744210; }
.badge-supprime { background: #fed7d7; color: #742a2a; }
.success-message, .error-message, .notice {
  padding: 10px 12px;
  border-radius: 8px;
  margin-bottom: 12px;
}
.success-message { color: #22543d; background: #c6f6d5; }
.error-message { color: #742a2a; background: #fed7d7; }
.notice { background: #f7fafc; color: #2d3748; font-weight: 700; }
.empty {
  color: #718096;
  font-size: 14px;
  padding: 10px 0;
}

@media (min-width: 720px) {
  .page { padding: 22px; }
  .page-header {
    grid-template-columns: minmax(0, 1fr) auto;
    align-items: start;
  }
  .filters {
    grid-template-columns: repeat(5, minmax(0, 1fr));
  }
  .grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
  .actions {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }
  .item-card {
    grid-template-columns: minmax(0, 1fr) auto;
  }
}

@media (min-width: 1040px) {
  .layout {
    grid-template-columns: minmax(320px, 420px) minmax(0, 1fr);
    align-items: start;
  }
}
</style>
